
const PRICE_ERROR = "Enter a price of at least $1.";
const RESERVED_SLUGS = ["widget"];

export const benefitKey = (benefit) => String(benefit.id || benefit._tempId);

export const visibleBenefits = (product) => (product.membership_product_benefits || []).filter((b) => !b._destroy);

export const DISPLAY = "display";
export const DOOR_ADMISSION = "door_admission";
export const NO_ORGANIZER_FEES = "no_organizer_fees";
export const NO_ORGANIZER_FEES_DESCRIPTION = "No organizer fees on tickets";
export const MAX_SCANS_PER_DAY = 100;
export const isDoorAdmission = (benefit) => benefit.kind === DOOR_ADMISSION;
export const isNoOrganizerFees = (benefit) => benefit.kind === NO_ORGANIZER_FEES;
export const validScansPerDay = (scans) => Number.isInteger(scans) && scans >= 1 && scans <= MAX_SCANS_PER_DAY;
export const scansErrorKey = (benefit) => `benefit:${benefitKey(benefit)}:scans`;
export const benefitTextForFans = (benefit) =>
  isDoorAdmission(benefit) && validScansPerDay(benefit.scans_per_day)
    ? `${benefit.description} · ${benefit.scans_per_day} ${benefit.scans_per_day === 1 ? "entry" : "entries"} a day`
    : benefit.description;
export const kindChange = (benefit, kind) => {
  if (kind === DOOR_ADMISSION) return { kind, scans_per_day: benefit.scans_per_day ?? 1 };
  if (kind === NO_ORGANIZER_FEES && !(benefit.description || "").trim()) return { kind, description: NO_ORGANIZER_FEES_DESCRIPTION };
  return { kind };
};

export const slugify = (name) =>
  String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const MIN_LOGINS = 2;
export const MAX_LOGINS = 10;
export const validLogins = (logins) => Number.isInteger(logins) && logins >= MIN_LOGINS && logins <= MAX_LOGINS;
export const DEFAULT_MAX_CHILDREN = 6;
const validMaxChildren = (max) => max === null || (Number.isInteger(max) && max >= 1);
const GUEST_SETTINGS = ["allow_children", "max_children", "allow_caregiver"];

export const newMembershipProduct = (venueOwnerships) => {
  const onlyVenueId = venueOwnerships.length === 1 ? venueOwnerships[0].id : null;
  return {
    name: "",
    description: "",
    billing_venue_ownership_id: onlyVenueId,
    add_to_event_ticketing: false,
    absorb_fees: true,
    listed_publicly: true,
    giftable: false,
    gift_buyers_can_keep_paying: false,
    collect_address: "off",
    collect_phone: "off",
    send_welcome_email: true,
    switch_to: "any",
    switchable_membership_product_ids: [],
    slug: "",
    household: false,
    household_logins: 2,
    status: "draft",
    venue_ownership_ids: onlyVenueId ? [onlyVenueId] : [],
    membership_product_benefits: [],
    default_price: { amount_cents: null, interval: "year", active: true },
    ticketing_price_record: null,
    default_monthly_price: null,
    ticketing_monthly_price_record: null,
  };
};

const cents = (record) => (record ? record.amount_cents : undefined);

// Mirrors MembershipProduct#annual_equivalent_cents: the annual price, or monthly × 12 without one.
export const annualEquivalentCents = (product) => cents(product.default_price) || (cents(product.default_monthly_price) || 0) * 12;
const centsOrNull = (record) => cents(record) || null;

export const buildFormData = (product, { annualEnabled, monthlyEnabled, imageFile } = {}) => {
  const formData = new FormData();
  const field = (name, value) => formData.append(`membership_product[${name}]`, value);

  field("name", product.name);
  field("description", product.description || "");
  field("billing_venue_ownership_id", product.billing_venue_ownership_id);
  field("add_to_event_ticketing", product.add_to_event_ticketing);
  field("absorb_fees", product.absorb_fees !== false);
  field("listed_publicly", product.listed_publicly !== false);
  field("giftable", !!product.giftable);
  field("gift_buyers_can_keep_paying", !!product.gift_buyers_can_keep_paying);
  field("collect_address", product.collect_address || "off");
  field("collect_phone", product.collect_phone || "off");
  field("send_welcome_email", product.send_welcome_email !== false);
  field("slug", product.slug || "");
  field("household", !!product.household);
  field("household_logins", product.household_logins ?? 2);
  const guestValue = (name) => (name === "max_children" && !validMaxChildren(product[name] ?? null) ? DEFAULT_MAX_CHILDREN : product[name] ?? "");
  GUEST_SETTINGS.filter((name) => name in product).forEach((name) => field(name, guestValue(name)));
  (product.venue_ownership_ids || []).forEach((id) => field("venue_ownership_ids][", id));
  field("switch_to", product.switch_to || "any");
  // An empty entry keeps the key present, so unpicking the last membership clears the list.
  field("switchable_membership_product_ids][", "");
  (product.switchable_membership_product_ids || []).forEach((id) => field("switchable_membership_product_ids][", id));

  product.membership_product_benefits.forEach((b, i) => {
    const attr = (name, value) => field(`membership_product_benefits_attributes][${i}][${name}`, value);
    if (b.id) attr("id", b.id);
    attr("description", b.description);
    attr("kind", b.kind);
    attr("scans_per_day", isDoorAdmission(b) ? b.scans_per_day ?? "" : "");
    attr("position", i);
    if (b._destroy) attr("_destroy", true);
  });

  const cadence = (enabled, defaultName, defaultRecord, ticketingName, ticketingRecord) => {
    if (enabled) {
      field(defaultName, cents(defaultRecord) || "");
      if (product.add_to_event_ticketing) field(ticketingName, cents(ticketingRecord) || "");
    } else {
      field(defaultName, "");
      field(ticketingName, "");
    }
  };
  cadence(annualEnabled, "default_annual_amount_cents", product.default_price, "ticketing_annual_amount_cents", product.ticketing_price_record);
  cadence(
    monthlyEnabled,
    "default_monthly_amount_cents",
    product.default_monthly_price,
    "ticketing_monthly_amount_cents",
    product.ticketing_monthly_price_record
  );

  if (imageFile) field("image", imageFile);
  if (product._delete_image) formData.append("remove_image", "true");

  return formData;
};

const slugError = (slug) => {
  const tidy = slugify(slug);
  if (/^\d+$/.test(tidy)) return "Use at least one letter.";
  if (RESERVED_SLUGS.includes(tidy)) return "That link is taken by Opendate. Pick another.";
  return null;
};

export const validate = (product, { annualEnabled, monthlyEnabled, isEditing }) => {
  const errors = {};
  const venueIds = product.venue_ownership_ids || [];

  if (!product.name || !product.name.trim()) errors.name = "Add a name.";
  if (!product.description || !product.description.trim()) errors.description = "Add a description fans will see.";

  if (!annualEnabled && !monthlyEnabled) errors.pricing = "Turn on annual or monthly pricing.";
  const priceCheck = (enabled, key, record, ticketingKey, ticketingRecord) => {
    if (!enabled) return;
    if (!(cents(record) >= 100)) errors[key] = PRICE_ERROR;
    if (product.add_to_event_ticketing && !(cents(ticketingRecord) >= 100)) errors[ticketingKey] = PRICE_ERROR;
  };
  priceCheck(annualEnabled, "default_annual", product.default_price, "ticketing_annual", product.ticketing_price_record);
  priceCheck(monthlyEnabled, "default_monthly", product.default_monthly_price, "ticketing_monthly", product.ticketing_monthly_price_record);

  visibleBenefits(product).forEach((b) => {
    if (!b.description || !b.description.trim()) errors[`benefit:${benefitKey(b)}`] = "Add text, or remove this line.";
    if (isDoorAdmission(b) && !validScansPerDay(b.scans_per_day)) errors[scansErrorKey(b)] = `Enter 1 to ${MAX_SCANS_PER_DAY} scans a day.`;
  });

  if (product.household && !validLogins(product.household_logins)) {
    errors.household_logins = `Household memberships include ${MIN_LOGINS} to ${MAX_LOGINS} logins.`;
  }
  if (product.household && product.allow_children && !validMaxChildren(product.max_children ?? null)) {
    errors.max_children = "Enter 1 or more, or tick No limit.";
  }

  if (!venueIds.length) errors.venues = "Pick at least one venue.";
  if (!product.billing_venue_ownership_id) errors.billing = "Pick the venue dues pay out to.";
  else if (isEditing && venueIds.length && !venueIds.includes(product.billing_venue_ownership_id))
    errors.billing = "The billing venue has to be one of the venues above.";

  const slug = slugError(product.slug);
  if (slug) errors.slug = slug;

  return errors;
};

export const sectionOf = (field) => {
  if (field.startsWith("benefit:")) return "benefits";
  if (["name", "description", "household_logins", "max_children"].includes(field)) return "details";
  if (["venues", "billing", "slug"].includes(field)) return "selling";
  return "pricing";
};

const sortedIds = (ids) => JSON.stringify((ids || []).slice().sort());
const benefitLines = (p) =>
  JSON.stringify(visibleBenefits(p).map((b) => [b.description, b.kind, isDoorAdmission(b) ? b.scans_per_day : null]));

export const changedFields = (original, current, { annualEnabled, monthlyEnabled, imageFile } = {}) => {
  const changed = [];
  const same = (a, b) => a === b;
  [
    "name",
    "description",
    "billing_venue_ownership_id",
    "add_to_event_ticketing",
    "absorb_fees",
    "listed_publicly",
    "giftable",
    "gift_buyers_can_keep_paying",
    "collect_address",
    "collect_phone",
    "send_welcome_email",
    "switch_to",
    "slug",
    "household",
    "household_logins",
    ...GUEST_SETTINGS,
  ].forEach((key) => {
    if (!same(original[key], current[key])) changed.push(key);
  });
  if (sortedIds(original.venue_ownership_ids) !== sortedIds(current.venue_ownership_ids)) changed.push("venues");
  if (sortedIds(original.switchable_membership_product_ids) !== sortedIds(current.switchable_membership_product_ids)) changed.push("switchable_membership_product_ids");
  [
    ["default_annual", "default_price"],
    ["ticketing_annual", "ticketing_price_record"],
    ["default_monthly", "default_monthly_price"],
    ["ticketing_monthly", "ticketing_monthly_price_record"],
  ].forEach(([name, key]) => {
    if (centsOrNull(original[key]) !== centsOrNull(current[key])) changed.push(name);
  });
  if (!!cents(original.default_price) !== annualEnabled) changed.push("annualEnabled");
  if (!!cents(original.default_monthly_price) !== monthlyEnabled) changed.push("monthlyEnabled");
  if (benefitLines(original) !== benefitLines(current)) changed.push("benefits");
  if (imageFile || current._image_changed || current._delete_image) changed.push("image");
  return changed;
};
